import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../ds/Icon';
import { LogoMark } from '../ds/LogoMark';
import LoginModal from '../common/LoginModal';

const FEATURES = [
    {
        ico: 'clock', tone: '',
        title: 'Automatic time tracking',
        body: 'The Copilot desktop app punches people in and out on its own — capturing work and idle time in the background. No manual timesheets, no nagging.',
    },
    {
        ico: 'activity', tone: 'is-teal',
        title: 'Productivity intelligence',
        body: 'Screens are captured, OCR-read and classified into dev vs. non-dev time, then rolled up into productive hours and a searchable activity timeline.',
    },
    {
        ico: 'layers', tone: '',
        title: 'Unified issues & sprints',
        body: 'GitLab and Jira issues in one board with estimate-vs-spent, sprint health and burndown — plus built-in planning poker for unbiased estimates.',
    },
    {
        ico: 'barChart', tone: 'is-amber',
        title: 'Utilisation & billing',
        body: 'See billable hours and utilisation per project and per person, with month-by-month projections — built for services teams that bill their time.',
    },
    {
        ico: 'calendar', tone: 'is-teal',
        title: 'People & leave',
        body: 'Zoho People sync keeps employees, attendance and leave in step — and auto-flags no-shows as leave so the calendar always reflects reality.',
    },
    {
        ico: 'zap', tone: '',
        title: 'AI Agent',
        body: 'Tag a GitLab issue “AI Agent” and Focuz drafts a branch and pull request for it automatically — your backlog starts clearing itself.',
        tag: 'Labs',
    },
];

const STEPS = [
    { t: 'Install Copilot', d: 'A lightweight desktop app for macOS, Windows and Linux. One-click login from the web.' },
    { t: 'Work as usual', d: 'It quietly records activity and check-ins in the background — privately, on the cadence you set.' },
    { t: 'Focuz makes sense of it', d: 'Activity is classified into productive hours and tied to your GitLab / Jira issues and sprints.' },
    { t: 'See the whole picture', d: 'Live dashboards for sprint health, team status, utilisation and leave — no spreadsheets.' },
];

const INTEGRATIONS = ['GitLab', 'Jira', 'Zoho People', 'Google Chat', 'Google Sheets', 'Google SSO', 'S3 / Spaces'];

const PERSONAS = [
    {
        t: 'Engineering managers',
        d: 'See who’s working on what, where the sprint really stands, and who needs help — without chasing status updates.',
        items: ['Sprint health & burndown', 'Team activity, live', 'Estimate vs. actual per ticket'],
    },
    {
        t: 'Software services & agencies',
        d: 'Turn engineering time into accurate utilisation and billable hours, with projections you can take to clients.',
        items: ['Utilisation by project & person', 'Productive / billable hours', 'Monthly utilisation projections'],
    },
    {
        t: 'Distributed & remote teams',
        d: 'Replace manual timesheets with automatic check-ins, and keep HR in sync without the busywork.',
        items: ['Auto punch in / out', 'Leave & attendance via Zoho', 'Daily nudges in Google Chat'],
    },
];

const MockRow = ({ name, pct, elapsed, color, pill, pillBg, pillColor }) => (
    <div className="lp-mock__row">
        <span className="lp-mock__name">{name}</span>
        <span className="lp-mock__track">
            <i style={{ width: `${pct}%`, background: color }} />
            <u style={{ left: `${elapsed}%` }} />
        </span>
        <span className="lp-mock__pill" style={{ background: pillBg, color: pillColor }}>{pill}</span>
    </div>
);

const Landing = () => {
    const navigate = useNavigate();
    // If a session token exists, skip the marketing page and go to the app.
    // (If the token is stale, Layout clears it and bounces back here — no loop.)
    useEffect(() => {
        if (localStorage.getItem('access_token')) {
            navigate('/dashboard', { replace: true });
        }
    }, [navigate]);

    const [showLogin, setShowLogin] = useState(false);
    const openLogin = () => setShowLogin(true);
    return (
        <div className="ds-scope lp">
            <nav className="lp-nav">
                <div className="lp__wrap lp-nav__inner">
                    <span className="lp-brand"><LogoMark size={28} /> Focuz</span>
                    <div className="lp-nav__links">
                        <a href="#features">Features</a>
                        <a href="#how">How it works</a>
                        <a href="#who">Who it’s for</a>
                    </div>
                    <div className="lp-nav__cta">
                        <Link className="ds-btn is-ghost is-sm" to="/pages?path=download">Download</Link>
                        <button type="button" className="ds-btn is-primary is-sm" onClick={openLogin}>Sign in</button>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <header className="lp__wrap lp-hero">
                <div>
                    <span className="ds-badge is-accent lp-eyebrow">Productivity intelligence for software teams</span>
                    <h1 className="lp-hero__title">Turn your team’s time into <em>shipped work</em> — without timesheets.</h1>
                    <p className="lp-hero__sub">
                        Focuz quietly captures how work actually happens, classifies it into productive hours,
                        and ties it to your GitLab and Jira sprints — so managers see real progress and
                        services teams bill with confidence.
                    </p>
                    <div className="lp-hero__cta">
                        <button type="button" className="ds-btn is-primary lp-btn-lg" onClick={openLogin}>Get started</button>
                        <Link className="ds-btn is-soft lp-btn-lg" to="/pages?path=download">Download the Copilot</Link>
                    </div>
                    <p className="lp-hero__note">Works with GitLab · Jira · Zoho People · Google Workspace</p>
                </div>

                <div className="lp-mock" aria-hidden="true">
                    <div className="lp-mock__bar"><span /><span /><span /></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--ds-space-3)' }}>
                        <strong style={{ fontFamily: 'var(--ds-font-display)', fontSize: 'var(--ds-text-xl)' }}>Sprint Health</strong>
                        <span className="lp-mock__pill" style={{ background: 'var(--ds-success-tint)', color: '#0f6b3c' }}>On track</span>
                    </div>
                    <MockRow name="Test-Maze" pct={78} elapsed={72} color="var(--ds-success)" pill="78%" pillBg="var(--ds-success-tint)" pillColor="#0f6b3c" />
                    <MockRow name="Frappe" pct={34} elapsed={72} color="var(--ds-danger)" pill="34%" pillBg="var(--ds-danger-tint)" pillColor="#9c241a" />
                    <MockRow name="Bi-Dashboard" pct={90} elapsed={72} color="var(--ds-success)" pill="90%" pillBg="var(--ds-success-tint)" pillColor="#0f6b3c" />
                    <MockRow name="Carq API" pct={52} elapsed={72} color="var(--ds-warning)" pill="52%" pillBg="var(--ds-warning-tint)" pillColor="#8a5500" />
                </div>
            </header>

            {/* Features */}
            <section className="lp-section" id="features">
                <div className="lp__wrap">
                    <div className="lp-shead">
                        <span className="ds-badge lp-eyebrow">One platform</span>
                        <h2>Everything from check-in to invoice</h2>
                        <p>Activity tracking, engineering metrics, HR and billing — connected, instead of six disconnected tools.</p>
                    </div>
                    <div className="lp-features">
                        {FEATURES.map(f => (
                            <div className="lp-feature" key={f.title}>
                                <span className={`lp-feature__ico ${f.tone}`}><Icon name={f.ico} size={22} /></span>
                                <h3>{f.title}</h3>
                                <p>{f.body}</p>
                                {f.tag && <span className="ds-badge is-accent lp-feature__tag">{f.tag}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="lp-section lp-section--tint" id="how">
                <div className="lp__wrap">
                    <div className="lp-shead">
                        <span className="ds-badge lp-eyebrow">How it works</span>
                        <h2>Set up in minutes, insight from day one</h2>
                        <p>No process change for your team — they keep working, Focuz does the rest.</p>
                    </div>
                    <div className="lp-steps">
                        {STEPS.map((s, i) => (
                            <div className="lp-step" key={s.t}>
                                <div className="lp-step__n">{String(i + 1).padStart(2, '0')}</div>
                                <h4>{s.t}</h4>
                                <p>{s.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className="lp-integrations" style={{ marginTop: 'var(--ds-space-12)' }}>
                        {INTEGRATIONS.map(name => <span className="lp-chip" key={name}><Icon name="plug" size={15} /> {name}</span>)}
                    </div>
                </div>
            </section>

            {/* Who it's for */}
            <section className="lp-section" id="who">
                <div className="lp__wrap">
                    <div className="lp-shead">
                        <span className="ds-badge lp-eyebrow">Who it’s for</span>
                        <h2>Built for teams that bill — and lead — their time</h2>
                    </div>
                    <div className="lp-personas">
                        {PERSONAS.map(p => (
                            <div className="lp-persona" key={p.t}>
                                <h3>{p.t}</h3>
                                <p>{p.d}</p>
                                <ul>{p.items.map(i => <li key={i}>{i}</li>)}</ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="lp__wrap lp-cta">
                <div className="lp-cta__box">
                    <h2>Stop guessing where the time went.</h2>
                    <p>Give your team automatic tracking and your leaders a live source of truth.</p>
                    <div style={{ display: 'flex', gap: 'var(--ds-space-3)', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button type="button" className="ds-btn is-primary lp-btn-lg" onClick={openLogin}>Get started</button>
                        <Link className="ds-btn is-ghost lp-btn-lg" to="/pages?path=download" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>Download the Copilot</Link>
                    </div>
                </div>
            </section>

            <footer className="lp-footer">
                <div className="lp__wrap lp-footer__inner">
                    <span className="lp-brand"><LogoMark size={28} /> Focuz</span>
                    <div>
                        <button type="button" className="lp-foot-link" onClick={openLogin}>Sign in</button>
                        <Link to="/pages?path=download">Download</Link>
                        <Link to="/pages?path=faq">FAQ</Link>
                        <Link to="/pages?path=privacy">Privacy</Link>
                        <Link to="/pages?path=terms">Terms</Link>
                    </div>
                    <span style={{ color: 'var(--ds-ink-faint)', fontSize: 'var(--ds-text-sm)' }}>© Focuz</span>
                </div>
            </footer>

            <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />
        </div>
    );
};

export default Landing;
